import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SimpleLayout from './components/Layout/SimpleLayout';
import SimpleChatInterface from './components/Chat/SimpleChatInterface';
import SimpleLLMSelector from './components/LLMSelector/SimpleLLMSelector';
import StatisticsPage from './pages/StatisticsPage';
import UsersManagementPage from './pages/UsersManagementPage';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { InstallPrompt } from './components/PWA/InstallPrompt';
import { ThemeProvider } from './contexts/ThemeContext';
import { LLMProvider } from './types';
import llmService, { setAuthToken as setLLMAuthToken } from './services/llmService';
import { setAuthToken as setVoiceAuthToken } from './services/voiceService';
import { useChatStore, useAuthStore } from './stores';

function App() {
  // Zustand store
  const {
    selectedLLM,
    selectedModel,
    availableModels,
    isLoading,
    error,
    setSelectedLLM,
    setAvailableModels,
    setLoading,
    setError,
    loadConversations,
  } = useChatStore();
  
  const { token, isAuthenticated } = useAuthStore();
  
  // Устанавливаем токен в сервисы (голос и LLM) при изменении
  // И загружаем диалоги пользователя из БД
  useEffect(() => {
    setVoiceAuthToken(token);
    setLLMAuthToken(token);
    
    // Загружаем диалоги только если пользователь авторизован
    if (token && isAuthenticated) {
      llmService.getConversations().then((conversations) => {
        console.log('📚 Загружено диалогов из БД:', conversations.length);
        loadConversations(conversations);
      }).catch((error) => {
        console.error('❌ Ошибка загрузки диалогов:', error);
      });
    } else {
      // При выходе очищаем диалоги
      loadConversations([]);
    }
  }, [token, isAuthenticated, loadConversations]);

  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const models = await llmService.getAvailableModels();
      setAvailableModels(models);
      
      // Если выбранная модель недоступна, выбираем первую доступную
      const isCurrentModelAvailable = models.some(m => m.id === selectedModel);
      if (!isCurrentModelAvailable && models.length > 0) {
        const firstModel = models[0];
        setSelectedLLM(firstModel.provider, firstModel.id);
      }
      
    } catch (error: any) {
      console.error('❌ Ошибка загрузки моделей:', error);
      
      // Если это ошибка авторизации, показываем соответствующее сообщение
      if (error?.isAuthError || error?.response?.status === 401) {
        setError('Требуется авторизация. Пожалуйста, войдите в систему.');
        // Если токена нет, не показываем ошибку загрузки, просто не загружаем модели
        if (!token) {
          setError(null);
        }
      } else if (error?.isRateLimit || error?.response?.status === 429 || error?.status === 429) {
        // Ошибка rate limiting - показываем понятное сообщение
        setError(error?.message || 'Превышен лимит запросов. Пожалуйста, подождите немного и обновите страницу.');
      } else {
        setError(error?.message || 'Не удалось загрузить список доступных моделей');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedModel, setLoading, setError, setAvailableModels, setSelectedLLM, token]);

  // Загружаем модели только если пользователь авторизован (эндпоинт требует авторизации)
  useEffect(() => {
    if (token && isAuthenticated) {
      // Небольшая задержка, чтобы убедиться, что токен установлен в llmService
      const timeoutId = setTimeout(() => {
        console.log('🔄 [App] Запуск загрузки моделей, токен:', token ? token.substring(0, 20) + '...' : 'отсутствует', 'авторизован:', isAuthenticated);
        loadModels();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    } else {
      // Если пользователь не авторизован, очищаем модели
      setAvailableModels([]);
      setError(null);
    }
  }, [token, isAuthenticated, loadModels, setAvailableModels, setError]);

  const handleLLMChange = (provider: LLMProvider, model: string) => {
    setSelectedLLM(provider, model);
  };

  if (isLoading) {
    return (
      <ThemeProvider>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '1.2rem'
        }}>
          Загрузка...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <SimpleLayout>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <SimpleLLMSelector
                    selectedLLM={selectedLLM}
                    selectedModel={selectedModel}
                    availableModels={availableModels}
                    onLLMChange={handleLLMChange}
                  />
                  
                  {error && (
                    <div style={{
                      padding: '1rem',
                      background: '#fef2f2',
                      color: '#dc2626',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem',
                      border: '1px solid #fecaca'
                    }}>
                      {error}
                    </div>
                  )}
                  
                  <SimpleChatInterface
                    selectedLLM={selectedLLM}
                    selectedModel={selectedModel}
                    availableModels={availableModels}
                  />
                </div>
              </SimpleLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/statistics" element={
            <ProtectedRoute>
              <SimpleLayout>
                <StatisticsPage />
              </SimpleLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/users" element={
            <ProtectedRoute>
              <SimpleLayout>
                <UsersManagementPage />
              </SimpleLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
      
      {/* PWA Install Prompt - показывается на всех страницах */}
      <InstallPrompt />
    </ThemeProvider>
  );
}

export default App;
